'use client';

import { createContext, type ComponentPropsWithRef, useContext, useId } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import { Button as UIButton } from '@/components/ui/button';
import { Field, FieldLabel } from '@/components/ui/field';
import { cn } from '@/lib/utils';
import { MultiInput, SingleInput } from '@/shared-ui/atoms';

const formInputFieldControlVariants = cva('w-full', {
    variants: {
        actionType: {
            button: 'flex items-stretch [&>[data-slot=input]]:flex-1 [&>[data-slot=input]:not(:last-child)]:rounded-r-none [&>[data-slot=input]:not(:last-child)]:border-r-0 [&>[data-slot=input-action]:not(:first-child)]:rounded-l-none data-[disabled=true]:[&>[data-slot=input]]:disabled:!bg-primary-foreground',
            icon: 'relative [&>[data-slot=input]]:w-full [&>[data-slot=input]]:pr-10',
            oneTimePassword: 'w-full',
            text: '',
        },
    },
    defaultVariants: {
        actionType: 'button',
    },
});

const helperVariants = cva(
    'text-[12px] tracking-[0.015rem] mt-1 font-light transition-colors duration-200 ease-in-out group-data-[disabled=true]/field:!text-muted-foreground',
    {
        variants: {
            validationState: {
                default: 'text-primary-black',
                success: 'text-success',
                error: 'text-destructive',
            },
        },
        defaultVariants: {
            validationState: 'default',
        },
    }
);

const FormInputFieldContext = createContext<{
    disabled: boolean;
    helperId?: string;
    inputId: string;
} | null>(null);

type RootProps = ComponentPropsWithRef<typeof Field> & {
    disabled?: boolean;
    helperId?: string;
    inputId?: string;
};

const Root = ({ className, disabled = false, helperId, inputId: explicitInputId, ref, ...props }: RootProps) => {
    const generatedId = useId();
    const inputId = explicitInputId ?? `form-input-field-${generatedId}`;

    return (
        <FormInputFieldContext.Provider value={{ disabled, helperId, inputId }}>
            <Field
                className={cn('gap-0', className)}
                data-disabled={disabled ? 'true' : undefined}
                ref={ref}
                {...props}
            />
        </FormInputFieldContext.Provider>
    );
};

Root.displayName = 'FormInputField.Root';

type LabelProps = ComponentPropsWithRef<typeof FieldLabel>;

const Label = ({ className, htmlFor, ref, ...props }: LabelProps) => {
    const context = useContext(FormInputFieldContext);

    return (
        <FieldLabel
            className={cn('tracking-[0.01rem] font-normal', className)}
            htmlFor={htmlFor ?? context?.inputId}
            ref={ref}
            {...props}
        />
    );
};

Label.displayName = 'FormInputField.Label';

type InputWrapperProps = ComponentPropsWithRef<'div'> & VariantProps<typeof formInputFieldControlVariants>;

const InputWrapper = ({ actionType, className, ref, ...props }: InputWrapperProps) => {
    const context = useContext(FormInputFieldContext);
    const resolvedActionType = actionType ?? 'button';
    const disabled = context?.disabled ?? false;

    return (
        <div
            className={cn(formInputFieldControlVariants({ actionType: resolvedActionType }), 'mt-1', className)}
            data-action-type={resolvedActionType}
            data-disabled={disabled ? 'true' : undefined}
            ref={ref}
            {...props}
        />
    );
};

InputWrapper.displayName = 'FormInputField.InputWrapper';

type InputProps = Omit<ComponentPropsWithRef<typeof SingleInput>, 'id' | 'aria-describedby' | 'disabled'>;

const Input = ({ className, placeholder, ref, validationState, ...props }: InputProps) => {
    const context = useContext(FormInputFieldContext);
    const disabled = context?.disabled ?? false;
    const describedBy = context?.helperId;

    return (
        <SingleInput
            {...props}
            aria-describedby={describedBy}
            disabled={disabled}
            id={context?.inputId}
            placeholder={placeholder}
            className={className}
            validationState={validationState}
            ref={ref}
        />
    );
};

Input.displayName = 'FormInputField.Input';

type OneTimePasswordProps = Omit<ComponentPropsWithRef<typeof MultiInput>, 'id' | 'aria-describedby' | 'disabled'>;

const OneTimePassword = ({ ref, ...props }: OneTimePasswordProps) => {
    const context = useContext(FormInputFieldContext);
    const disabled = context?.disabled ?? false;
    const describedBy = context?.helperId;

    return <MultiInput {...props} aria-describedby={describedBy} disabled={disabled} id={context?.inputId} ref={ref} />;
};

OneTimePassword.displayName = 'FormInputField.OneTimePassword';

type ButtonProps = Omit<ComponentPropsWithRef<typeof UIButton>, 'disabled'>;

const Button = ({ className, ref, children, ...props }: ButtonProps) => {
    const context = useContext(FormInputFieldContext);
    const disabled = context?.disabled ?? false;

    return (
        <UIButton
            data-slot="input-action"
            disabled={disabled}
            variant="outline"
            className={cn(
                'tracking-[1.68px] h-12 bg-primary font-normal text-primary-foreground text-sm uppercase px-4 border-primary',
                'hover:text-primary-foreground hover:bg-primary-hover',
                'disabled:!border-snk-keyline-grey disabled:!bg-snk-disabled disabled:!text-muted-foreground disabled:!opacity-100',
                className
            )}
            ref={ref}
            {...props}>
            {children}
        </UIButton>
    );
};

Button.displayName = 'FormInputField.Button';

const IconWrapper = ({ className, ref, children, ...props }: ComponentPropsWithRef<'div'>) => {
    return (
        <div
            data-slot="input-icon-wrapper"
            className={cn('pointer-events-none absolute inset-y-0 right-0 flex items-center pr-4', className)}
            ref={ref}
            {...props}>
            {children}
        </div>
    );
};

IconWrapper.displayName = 'FormInputField.IconWrapper';

type HelperProps = Omit<ComponentPropsWithRef<'p'>, 'id'> & VariantProps<typeof helperVariants>;

const Helper = ({ className, ref, children, validationState, ...props }: HelperProps) => {
    const context = useContext(FormInputFieldContext);

    return (
        <p {...props} className={cn(helperVariants({ validationState }), className)} id={context?.helperId} ref={ref}>
            {children}
        </p>
    );
};

Helper.displayName = 'FormInputField.Helper';

const FormInputField = {
    Root,
    Label,
    InputWrapper,
    Input,
    OneTimePassword,
    Button,
    IconWrapper,
    Helper,
} as const;

export { FormInputField };
